import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import SectionHeading from '../home/SectionHeading';
import '../home/home.css';
import './thesisSpotlight.css';

const ease = [0.23, 1, 0.32, 1] as const;

const metrics = [
  { value: '100', label: 'Learned scalar parameters' },
  { value: '20', label: 'Unrolled ISTA layers' },
  { value: '~2×', label: 'Peak MSE improvement' },
  { value: '0.59', label: 'Baseline leakage floor' },
];

const findings = [
  {
    title: 'Classical signal processing, made trainable.',
    body: 'BEADS is recast as a compact neural architecture where every layer keeps physically meaningful parameters instead of hiding the work inside a black box.',
  },
  {
    title: 'Adaptivity helps, but the floor is real.',
    body: 'The learned model improves reconstruction without per-signal tuning, while the Baseline Leakage Index stubbornly plateaus across loss configurations.',
  },
  {
    title: 'Inductive bias beats loss engineering.',
    body: 'When dense chromatographic peaks violate the sparsity assumption, the inherited soft-thresholding operator defines the performance limit.',
  },
];

type ThesisSpotlightProps = {
  embedded?: boolean;
};

const ThesisSpotlight = ({ embedded = false }: ThesisSpotlightProps) => {
  const content = (
    <>
      <SectionHeading title="Thesis" />

      <div className="home-section-body">
        <motion.div
          className="thesis-panel"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease }}
        >
          <div className="thesis-hero">
            <div className="thesis-copy">
              <h3 className="thesis-title">
                LBEADS-NET: Algorithm unrolling for learned baseline estimation and
                denoising in chromatographic signals
              </h3>
              <p className="thesis-lede">
                Master&apos;s thesis at NYU Tandon, advised by Ivan Selesnick. Translates
                BEADS into a learned optimizer, measures what improves, then names the
                structural reason the model cannot fully escape baseline leakage.
              </p>

              <div className="home-action-row thesis-actions">
                <a
                  className="home-action home-action-primary"
                  href="/Thesis/lbeads-net-thesis.pdfs"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read thesis
                  <ArrowUpRight />
                </a>
                <a
                  className="home-action"
                  href="/Thesis/lbeads-net-defense.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Defense deck
                  <ArrowUpRight />
                </a>
              </div>
            </div>

            <dl className="thesis-metrics">
              {metrics.map((metric) => (
                <div key={metric.label} className="thesis-metric">
                  <dd>{metric.value}</dd>
                  <dt>{metric.label}</dt>
                </div>
              ))}
            </dl>
          </div>

          <div className="thesis-findings">
            {findings.map((finding) => (
              <article key={finding.title}>
                <h4>{finding.title}</h4>
                <p>{finding.body}</p>
              </article>
            ))}
          </div>
        </motion.div>
      </div>
    </>
  );

  if (embedded) {
    return <div className="home-about-module thesis-spotlight" id="Thesis">{content}</div>;
  }

  return (
    <section className="home-section thesis-spotlight" id="Thesis">
      <div className="home-section-inner">{content}</div>
    </section>
  );
};

export default ThesisSpotlight;
